<template>
  <section ref="rootRef" data-testid="insights-perspective-competition">
    <div data-testid="insights-competition-competitors">
      <InsightsMetricCard
        class="h-full"
        :headline="t('Reviews.product.insights.competition.competitors.title')"
        :subheadline="t('Reviews.product.insights.competition.competitors.subtitle')"
        :tooltip="t('Reviews.product.insights.competition.competitors.tooltip')"
        data-testid="insights-competition-competitors-card"
      >
        <InsightsDataState
          :loading="loading || productsLoading"
          :state="states.competitors"
          data-testid="insights-competition-competitors-data"
        >
          <p class="txt-body-s-regular text-subtle" data-testid="insights-competition-competitors-caption">{{ competitorsCaption }}</p>
          <InsightsBarRows
            v-if="ready && tokens"
            class="mt-4"
            :rows="competitorRows"
            :track-color="tokens.gridLine"
            :locale="locale"
            data-testid="competition-competitors"
          />
        </InsightsDataState>
      </InsightsMetricCard>
    </div>

    <div class="mt-6 grid grid-cols-1 gap-6 tablet:grid-cols-2">
      <div class="h-full" data-testid="insights-competition-benchmark">
        <InsightsMetricCard
          class="h-full"
          :headline="t('Reviews.product.insights.competition.benchmark.title')"
          :subheadline="t('Reviews.product.insights.competition.benchmark.subtitle')"
          :tooltip="t('Reviews.product.insights.competition.benchmark.tooltip')"
          data-testid="insights-competition-benchmark-card"
        >
          <InsightsDataState :loading="loading" :state="states.benchmark" data-testid="insights-competition-benchmark-data">
            <p class="txt-body-s-regular text-subtle" data-testid="insights-competition-benchmark-caption">{{ benchmarkCaption }}</p>
            <InsightsBenchmarkChart
              v-if="ready && tokens"
              class="mt-4"
              :categories="benchmarkCategories"
              :values="benchmark.values"
              :color="tokens.palette[3]"
              :highlight-color="tokens.palette[0]"
              :highlight-index="0"
              grid-lines
              :locale="locale"
              data-testid="competition-benchmark"
            />
          </InsightsDataState>
        </InsightsMetricCard>
      </div>

      <div class="h-full" data-testid="insights-competition-visibility">
        <InsightsMetricCard
          class="h-full"
          :headline="t('Reviews.product.insights.competition.visibility.title')"
          :subheadline="t('Reviews.product.insights.competition.visibility.subtitle')"
          :tooltip="t('Reviews.product.insights.competition.visibility.tooltip')"
          data-testid="insights-competition-visibility-card"
        >
          <InsightsDataState
            :loading="loading || productsLoading"
            :state="states.visibility"
            data-testid="insights-competition-visibility-data"
          >
            <InsightsHeroMetric
              :value="visibility.sharePercent"
              :trend="visibilityTrend"
              :caption="visibilityCaption"
              data-testid="insights-competition-visibility-hero"
            />
            <p class="mt-4 text-right txt-body-s-regular text-subtle">
              {{ t('Reviews.product.insights.competition.visibility.chartHint') }}
            </p>
            <InsightsStackedBar
              v-if="ready && tokens"
              class="mt-2"
              :segments="visibilitySegments"
              :locale="locale"
              data-testid="competition-visibility"
            />
          </InsightsDataState>
        </InsightsMetricCard>
      </div>
    </div>

    <div class="mt-6" data-testid="insights-competition-attributes">
      <InsightsMetricCard
        :headline="t('Reviews.product.insights.competition.attributes.title')"
        :subheadline="t('Reviews.product.insights.competition.attributes.subtitle')"
        :tooltip="t('Reviews.product.insights.competition.attributes.footnote')"
        data-testid="insights-competition-attributes-card"
      >
        <InsightsDataState :loading="loading" :state="states.attributes" data-testid="insights-competition-attributes-data">
          <div v-if="ready && tokens" class="grid grid-cols-1 gap-x-8 gap-y-6 tablet:grid-cols-2" data-testid="competition-attributes">
            <InsightsAttributeBar
              v-for="(attribute, index) in attributes"
              :key="attribute.key"
              :label="t(`Reviews.product.insights.reviews.attributes.${camelCase(attribute.key)}`)"
              :value="attribute.value"
              :comparison="attribute.comparison"
              :above-label="t('Reviews.product.insights.competition.attributes.aboveLabel')"
              :below-label="t('Reviews.product.insights.competition.attributes.belowLabel')"
              :color="tokens.palette[0]"
              :comparison-color="tokens.dataLabel"
              :track-color="tokens.gridLine"
              :locale="locale"
              :data-testid="`competition-attribute-${index}`"
            />
          </div>
          <div
            v-if="ready && tokens"
            class="mt-6 flex items-center gap-4 txt-body-s-regular text-subtle"
            data-testid="insights-competition-attributes-legend"
          >
            <span class="flex items-center gap-1.5">
              <svg width="12" height="12" aria-hidden="true"><rect width="12" height="12" rx="2" :fill="tokens.palette[0]" /></svg>
              {{ t('Reviews.product.insights.competition.attributes.legendYou') }}
            </span>
            <span class="flex items-center gap-1.5">
              <svg width="12" height="12" aria-hidden="true"><rect x="5" width="2" height="12" :fill="tokens.dataLabel" /></svg>
              {{ t('Reviews.product.insights.competition.attributes.legendComparison') }}
            </span>
          </div>
        </InsightsDataState>
      </InsightsMetricCard>
    </div>

    <div class="mt-6" data-testid="insights-competition-pricing">
      <InsightsMetricCard
        :headline="t('Reviews.product.insights.competition.pricing.title')"
        :subheadline="t('Reviews.product.insights.competition.pricing.subtitle')"
        :tooltip="pricingTooltip"
        data-testid="insights-competition-pricing-card"
      >
        <template v-if="!loading && states.pricing === 'ready'" #badge>
          <ChipBadge color="default" rounded size="s" data-testid="insights-competition-pricing-product-count">
            {{ t('Reviews.product.insights.competition.pricing.productCount', { count: pricing.productsInCategory }) }}
          </ChipBadge>
        </template>
        <InsightsDataState :loading="loading" :state="states.pricing" data-testid="insights-competition-pricing-data">
          <InsightsBenchmarkChart
            v-if="ready && tokens"
            :categories="pricingDecileLabels"
            :values="pricing.values"
            :color="tokens.muted"
            :highlight-color="tokens.palette[0]"
            :highlight-index="ownDecileIndex"
            :format-value="formatCurrency"
            grid-lines
            cap-outliers
            :locale="locale"
            data-testid="competition-pricing"
          />
        </InsightsDataState>
      </InsightsMetricCard>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ChipBadge, useCurrencyFormatter } from '@ramp106/omrjs-core-ui'
import { camelCase } from 'lodash-es'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useOmrEchartsTheme } from '@/composables/useOmrEchartsTheme'
import type { ProductById } from '@/gql/reviews'
import type {
  CompetitionBenchmarkData,
  CompetitionAttributeItem,
  CompetitionCompetitorRow,
  CompetitionInsightsView,
  CompetitionPricingData,
  CompetitionVisibilityCaption,
  CompetitionVisibilityData,
} from '@/gql/reviews/mappers/insights'
import { makeRatioPercent } from '@/helpers/chartFormat'

const props = withDefaults(
  defineProps<{
    product: ProductById
    benchmark: CompetitionBenchmarkData
    visibility: CompetitionVisibilityData
    competitors: CompetitionCompetitorRow[]
    competitorsCaption: CompetitionVisibilityCaption
    attributes: CompetitionAttributeItem[]
    pricing: CompetitionPricingData
    states: CompetitionInsightsView['states']
    loading?: boolean
    productsLoading?: boolean
  }>(),
  { loading: false, productsLoading: false },
)

const { t, locale } = useI18n()
const { formatCurrency } = useCurrencyFormatter()
const { rootRef, tokens, ready } = useOmrEchartsTheme()
const visibilityTrend = computed(() => {
  const trend = props.visibility.trend
  if (!trend) return undefined
  return { value: t('Reviews.product.insights.competition.visibility.sovDelta', { count: trend.value }), direction: trend.direction }
})

const windowCaption = ({ span, compared, params }: CompetitionVisibilityCaption) =>
  t(`Reviews.product.insights.competition.visibility.caption.${span}${compared ? '' : 'NoComparison'}`, params)

const visibilityCaption = computed(() => windowCaption(props.visibility.caption))

const competitorsCaption = computed(() => windowCaption(props.competitorsCaption))

const benchmarkCaption = computed(() => windowCaption(props.benchmark.caption))

const benchmarkCategories = computed(() => [
  t('Reviews.product.insights.competition.benchmark.you'),
  t('Reviews.product.insights.competition.benchmark.top3'),
  t('Reviews.product.insights.competition.benchmark.top10'),
  t('Reviews.product.insights.competition.benchmark.categoryAvg'),
])

const visibilitySegments = computed(() => {
  const theme = tokens.value
  if (!theme) return []
  const competitorColors = [theme.palette[1], theme.palette[2], theme.palette[4], theme.palette[3]]
  let competitorIndex = 0
  return props.visibility.segments.map((segment) => {
    if (segment.role === 'you') {
      return { label: t('Reviews.product.insights.competition.visibility.segmentYou'), value: segment.value, color: theme.palette[0] }
    }
    if (segment.role === 'others') {
      return { label: t('Reviews.product.insights.competition.visibility.segmentOthers'), value: segment.value, color: theme.axisLine }
    }
    const color = competitorColors[competitorIndex++ % competitorColors.length]
    return { label: segment.label ?? '', value: segment.value, color }
  })
})

const ownDecileIndex = computed(() => {
  const own = props.pricing.own
  return own ? props.pricing.deciles.indexOf(own.decile) : undefined
})

const pricingDecileLabels = computed(() => {
  const percent = makeRatioPercent(locale.value, 0)
  const you = t('Reviews.product.insights.competition.pricing.you')
  return props.pricing.deciles.map((decile, index) => percent(decile / 10) + (index === ownDecileIndex.value ? `\n${you}` : ''))
})

const pricingReadme = computed(() => {
  const readme = t('Reviews.product.insights.competition.pricing.readme')
  const { own, ownUnpriced, categoryAverageMonthlyEur } = props.pricing
  if (ownUnpriced) return `${readme} ${t('Reviews.product.insights.competition.pricing.ownUnpriced', { product: props.product.title })}`
  if (!own || categoryAverageMonthlyEur === null) return readme
  const ownPosition = t('Reviews.product.insights.competition.pricing.ownPosition', {
    product: props.product.title,
    price: formatCurrency(own.priceMonthlyEur, { fractionDigits: Number.isInteger(own.priceMonthlyEur) ? 0 : 2 }),
    average: formatCurrency(categoryAverageMonthlyEur),
  })
  return `${readme} ${ownPosition}`
})

const pricingTooltip = computed(() =>
  !props.loading && props.states.pricing === 'ready' ? pricingReadme.value : t('Reviews.product.insights.competition.pricing.readme'),
)

const competitorRows = computed(() => {
  const theme = tokens.value
  if (!theme) return []
  const rowColors = [theme.palette[0], theme.palette[2], theme.palette[1], theme.palette[3], theme.axisLine]
  return props.competitors.map((row, index) => ({
    label: row.label,
    value: row.value,
    color: rowColors[index % rowColors.length],
  }))
})
</script>
